import React from 'react';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ChevronLeft, ChevronRight, Clock, Phone, Building, 
  Calendar as CalendarIcon, CheckCircle, XCircle, AlertCircle 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { STATUS_CONFIG, TIME_SLOTS } from './constants';

// Generate timeline hours from 7:00 to 15:30 with 15-minute intervals
const TIMELINE_HOURS = [
  '07:00', '07:15', '07:30', '07:45',
  '08:00', '08:15', '08:30', '08:45',
  '09:00', '09:15', '09:30', '09:45',
  '10:00', '10:15', '10:30', '10:45',
  '11:00', '11:15', '11:30', '11:45',
  '12:00', '12:15', '12:30', '12:45',
  '13:00', '13:15', '13:30', '13:45',
  '14:00', '14:15', '14:30', '14:45',
  '15:00', '15:15', '15:30'
];

const DayView = ({
  currentDate,
  selectedDate,
  appointments,
  selectedDateAppointments,
  onDayClick,
  onAppointmentClick,
  onPrevMonth,
  onNextMonth,
  onNewAppointment,
  days,
  emptyDays
}) => {
  const getAppointmentsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.date === dateStr);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <Card className="lg:col-span-2 bg-white border-slate-100 shadow-soft">
        <CardHeader className="pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-brand-main" strokeWidth={1.5} />
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={onPrevMonth}
                className="border-slate-200 hover:bg-slate-100"
                data-testid="prev-month-button"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onNextMonth}
                className="border-slate-200 hover:bg-slate-100"
                data-testid="next-month-button"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {emptyDays.map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square" />
            ))}
            {days.map((day) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isSelected = isSameDay(day, selectedDate);
              const isDayToday = isSameDay(day, new Date());
              
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => onDayClick(day)}
                  className={`aspect-square p-1 rounded-lg transition-all relative group ${
                    isSelected 
                      ? 'bg-[#1a5192] text-white font-bold' 
                      : isDayToday
                      ? 'bg-[#00b4d8] text-white font-bold'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <span className={`text-sm font-medium ${isSelected || isDayToday ? 'text-white' : ''}`}>
                    {format(day, 'd')}
                  </span>
                  {dayAppointments.length > 0 && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dayAppointments.slice(0, 3).map((appt, i) => (
                        <div 
                          key={i} 
                          className={`w-1.5 h-1.5 rounded-full ${
                            isSelected || isDayToday ? 'bg-white' : 
                            appt.status === 'COMPLETADA' ? 'bg-emerald-500' :
                            appt.status === 'CANCELADA' ? 'bg-red-500' :
                            appt.status === 'NO_ASISTIO' ? 'bg-amber-500' :
                            'bg-blue-500'
                          }`} 
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day View / Timeline */}
      <Card className="bg-white border-slate-100 shadow-soft">
        <CardHeader className="pb-4 border-b border-slate-100">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900">
            {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {selectedDateAppointments.length} cita(s) programada(s)
          </p>
        </CardHeader>
        <CardContent className="p-0 max-h-[500px] overflow-y-auto">
          {/* Timeline View */}
          <div className="relative" data-testid="day-timeline">
            {TIMELINE_HOURS.map((time, index) => {
              // Find appointment that starts at this time
              const appointment = selectedDateAppointments.find(apt => apt.hora_inicio === time);
              const isHourMark = time.endsWith(':00');
              
              return (
                <div 
                  key={time} 
                  className={`flex items-start border-b border-slate-100 ${isHourMark ? 'bg-slate-50/50' : ''}`}
                >
                  {/* Time label */}
                  <div className={`w-16 flex-shrink-0 py-2 px-2 text-right ${isHourMark ? 'font-medium text-slate-700' : 'text-slate-400'}`}>
                    <span className="text-xs">{time}</span>
                  </div>
                  
                  {/* Appointment slot */}
                  <div className="flex-1 min-h-[40px] border-l border-slate-200 py-1 px-2">
                    {appointment && (
                      <div 
                        className={`rounded-lg p-2 cursor-pointer transition-all hover:shadow-md ${
                          appointment.status === 'COMPLETADA' ? 'bg-emerald-50 border-l-4 border-emerald-500' :
                          appointment.status === 'CANCELADA' ? 'bg-red-50 border-l-4 border-red-500' :
                          appointment.status === 'NO_ASISTIO' ? 'bg-amber-50 border-l-4 border-amber-500' :
                          appointment.status === 'REPROGRAMADA' ? 'bg-purple-50 border-l-4 border-purple-500' :
                          'bg-blue-50 border-l-4 border-blue-500'
                        }`}
                        onClick={() => onAppointmentClick(appointment)}
                        data-testid={`appointment-item-${appointment.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-[#1a5192]/10 text-[#1a5192] border-0 font-mono font-medium text-xs">
                            {appointment.hora_inicio} - {appointment.hora_fin}
                          </Badge>
                          <Badge className={`${STATUS_CONFIG[appointment.status]?.color || STATUS_CONFIG.PROGRAMADA.color} border-0 text-xs`}>
                            {STATUS_CONFIG[appointment.status]?.label || 'Programada'}
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-900 text-sm">
                          {appointment.patient_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {appointment.tipo_examen}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {appointment.telefono}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {appointment.institucion}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Empty state overlay if no appointments */}
            {selectedDateAppointments.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                <div className="text-center p-6">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400">No hay citas para este día</p>
                  <Button 
                    variant="link" 
                    onClick={onNewAppointment}
                    className="text-brand-main mt-2"
                  >
                    Agendar una cita
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DayView;
